import React, { useState } from "react";

type TIntegerInputProps = {
    defaultValue: number;
    onUpdate: (value: number) => void;
    disabled?: boolean;
    classNames?: string;
};

const IntegerInput = ({
    defaultValue,
    onUpdate,
    disabled = false,
    classNames = "",
}: TIntegerInputProps) => {
    // Held as a string so a half-typed value ("", "-") survives until blur/Enter
    // instead of being coerced back to a number on every keystroke.
    const [value, setValue] = useState(String(defaultValue));

    // Replaces componentDidUpdate(prevProps), which re-synced only when the
    // incoming value changed -- deliberately leaving the local value alone while
    // the user is mid-edit. Tracking the previous prop keeps that distinction;
    // a plain `defaultValue !== value` check would stomp on in-progress input.
    const [prevDefault, setPrevDefault] = useState(defaultValue);
    if (defaultValue !== prevDefault) {
        setPrevDefault(defaultValue);
        setValue(String(defaultValue));
    }

    const getValue = () => parseInt(value, 10);

    const returnValue = () => {
        let val = getValue();
        if (isNaN(val)) {
            val = defaultValue;
        }
        setValue(String(val));
        onUpdate(val);
    };

    const onChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        setValue(evt?.target.value);
    };

    const onKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
        if (evt.key !== "ArrowDown" && evt.key !== "ArrowUp" && evt.key !== "Enter") {
            return;
        }

        const val = getValue();
        if (evt.key === "ArrowUp") {
            setValue(String(val + 1));
        } else if (evt.key === "ArrowDown") {
            setValue(String(val - 1));
        } else if (evt.key === "Enter") {
            returnValue();
            evt.preventDefault();
        }

        evt.stopPropagation();
    };

    return (
        <input
            className={classNames}
            disabled={disabled}
            type="text"
            pattern="[\-0-9]*"
            value={value}
            onChange={onChange}
            onBlur={returnValue}
            onKeyDown={onKeyDown}
        />
    );
};

export default IntegerInput;
